import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, FolderKanban, CalendarClock, DollarSign, CheckSquare, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell } from "recharts";
import api from "../../lib/api";
import { motion } from "framer-motion";
import { formatDistanceToNow } from "date-fns";

const CARDS = [
  { key: "new_leads", label: "New Leads", icon: Users, to: "/admin/leads", tone: "text-brand-purple bg-brand-light" },
  { key: "pipeline_value", label: "Pipeline Value", icon: TrendingUp, to: "/admin/leads", tone: "text-brand-orange bg-brand-orangeLight", money: true },
  { key: "active_projects", label: "Active Projects", icon: FolderKanban, to: "/admin/projects", tone: "text-blue-600 bg-blue-50" },
  { key: "upcoming", label: "Upcoming", icon: CalendarClock, to: "/admin/projects", tone: "text-amber-600 bg-amber-50" },
  { key: "revenue", label: "Project Revenue", icon: DollarSign, to: "/admin/projects", tone: "text-green-600 bg-green-50", money: true },
  { key: "open_tasks", label: "Open Tasks", icon: CheckSquare, to: "/admin/tasks", tone: "text-brand-purpleDeep bg-brand-light" },
];

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [mine, setMine] = useState([]);
  useEffect(() => { api.get("/dashboard").then(({ data }) => setD(data)); api.get("/my-shoots").then(({ data }) => setMine(data.filter((x) => x.upcoming))).catch(() => {}); }, []);
  if (!d) return <div className="text-brand-purpleDeep/40">Loading…</div>;

  const pipeline = Object.entries(d.pipeline).filter(([n]) => n !== "Lost").map(([name, value]) => ({ name, value }));
  const colors = ["#821AC8", "#9B3DD6", "#B45FE0", "#FF6500", "#22c55e", "#ef4444"];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading font-extrabold text-3xl text-brand-purpleDeep">Dashboard</h1>
      <p className="mt-1 text-brand-purpleDeep/50">Your video business at a glance.</p>
      {mine.length > 0 && <div className="mt-6 bg-white rounded-2xl border border-border p-5" data-testid="my-shoots"><h3 className="font-heading font-bold mb-2">My upcoming shoots</h3>{mine.slice(0, 6).map((s) => <Link key={s.id} to={`/admin/projects/${s.id}/crew`} className="flex justify-between text-sm py-1.5 border-b border-border last:border-0 hover:text-brand-purple" data-testid={`my-shoot-${s.id}`}><span>{s.name}{s.lead && <span className="ml-2 text-[10px] font-bold bg-brand-light text-brand-purple px-1.5 py-0.5 rounded">Lead</span>}</span><span className="text-brand-purpleDeep/60">{s.shoot_date || "Date TBC"} {s.shoot_slot ? `· ${s.shoot_slot}` : ""} · {s.region}</span></Link>)}</div>}

      <div className="mt-8 grid gap-4 grid-cols-2 lg:grid-cols-6">
        {CARDS.map((c, i) => (
          <motion.div key={c.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Link to={c.to} className="block bg-white rounded-xl border border-border p-5 hover:shadow-[0_10px_30px_rgba(130,26,200,0.08)] transition-shadow" data-testid={`stat-${c.key}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.tone}`}><c.icon size={20} /></div>
              <div className="mt-4 font-heading font-extrabold text-2xl text-brand-purpleDeep">
                {c.money ? `$${Number(d[c.key] || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : d[c.key]}
              </div>
              <div className="text-sm text-brand-purpleDeep/50">{c.label}</div>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 bg-white rounded-xl border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={18} className="text-brand-purple" />
            <h2 className="font-heading font-bold text-brand-purpleDeep">Lead Pipeline</h2>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={pipeline}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#4B3E54" }} axisLine={false} tickLine={false} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {pipeline.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <h2 className="font-heading font-bold text-brand-purpleDeep mb-4">Recent Activity</h2>
          <div className="space-y-4">
            {d.recent_activity.length === 0 && <p className="text-sm text-brand-purpleDeep/40">No activity yet.</p>}
            {d.recent_activity.map((a) => (
              <div key={a.id} className="flex gap-3">
                <div className="w-2 h-2 rounded-full bg-brand-orange mt-1.5 shrink-0" />
                <div>
                  <div className="text-sm text-brand-purpleDeep">{a.description}</div>
                  <div className="text-xs text-brand-purpleDeep/40">{a.created_at ? formatDistanceToNow(new Date(a.created_at), { addSuffix: true }) : ""}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
